import { Database } from 'types/supabase'
import { supabaseClient } from 'supabase'
import { uploadImage } from 'supabase/files'

import { CreativeDetailRow } from './createCreative'

export type UpdateCreativeParams = {
  id: string
  title?: Database['public']['Tables']['creatives']['Row']['title']
  description?: Database['public']['Tables']['creatives']['Row']['description']
  cta_text?: Database['public']['Tables']['creatives']['Row']['cta_text']
  cta_link?: Database['public']['Tables']['creatives']['Row']['cta_link']
  // Which advertiser contact the mobile "Book here" CTA opens
  cta_channel?: Database['public']['Tables']['creatives']['Row']['cta_channel']
  // 1:1 square (ad card) / 16:9 wide (ad detail hero) — re-upload only what changed
  card_image_file?: File
  detail_image_file?: File
  // MVP-7 detail-page content (all optional; null clears the field)
  description_pt?: string | null
  price_from?: number | null
  price_currency?: string | null
  price_caption?: string | null
  highlights?: string[] | null
  details?: CreativeDetailRow[] | null
  location_label?: string | null
  location_lat?: number | null
  location_long?: number | null
  badge_label?: string | null
  advertiser_caption?: string | null
  // Short city name shown on the in-feed ad card
  city?: string | null
  // Bilingual: PT variants of the content fields
  title_pt?: string | null
  cta_text_pt?: string | null
  price_caption_pt?: string | null
  highlights_pt?: string[] | null
  badge_label_pt?: string | null
  advertiser_caption_pt?: string | null
  // Viewable via the in-app preview deep link (not shown in the feed)
  is_preview?: boolean
  // Promo/discount code shown on the mobile detail page (toggle + code + caption)
  promo_enabled?: boolean
  promo_code?: string | null
  promo_caption?: string | null
  promo_caption_pt?: string | null
}

export const updateCreative = async ({
  id,
  title,
  description,
  cta_text,
  cta_link,
  cta_channel,
  card_image_file,
  detail_image_file,
  description_pt,
  price_from,
  price_currency,
  price_caption,
  highlights,
  details,
  location_label,
  location_lat,
  location_long,
  badge_label,
  advertiser_caption,
  city,
  title_pt,
  cta_text_pt,
  price_caption_pt,
  highlights_pt,
  badge_label_pt,
  advertiser_caption_pt,
  is_preview,
  promo_enabled,
  promo_code,
  promo_caption,
  promo_caption_pt,
}: UpdateCreativeParams) => {
  const updateData: Partial<Database['public']['Tables']['creatives']['Update']> = {
    title,
    description,
    cta_text,
    cta_link,
    cta_channel,
    description_pt,
    price_from,
    price_currency,
    price_caption,
    highlights,
    details,
    location_label,
    location_lat,
    location_long,
    badge_label,
    advertiser_caption,
    city,
    title_pt,
    cta_text_pt,
    price_caption_pt,
    highlights_pt,
    badge_label_pt,
    advertiser_caption_pt,
    is_preview,
    promo_enabled,
    promo_code,
    promo_caption,
    promo_caption_pt,
    updated_at: new Date().toISOString(),
  }

  // Re-upload only the images that changed (crop enforces the ratios client-side).
  if (card_image_file || detail_image_file) {
    // Get the current creative to determine advertiser_id for folder structure
    const { data: currentCreative, error: fetchError } = await supabaseClient
      .from('creatives')
      .select('advertiser_id')
      .eq('id', id)
      .single()

    if (fetchError) {
      throw fetchError
    }

    if (card_image_file) {
      const cardImageUrl = await uploadImage(
        card_image_file,
        `${Date.now()}-card.${card_image_file.type.split('/')[1]}`,
        'creatives',
        currentCreative.advertiser_id
      )

      if (!cardImageUrl) {
        throw new Error('Failed to upload card image')
      }

      updateData.card_image_url = cardImageUrl
    }

    if (detail_image_file) {
      const detailImageUrl = await uploadImage(
        detail_image_file,
        `${Date.now()}-detail.${detail_image_file.type.split('/')[1]}`,
        'creatives',
        currentCreative.advertiser_id
      )

      if (!detailImageUrl) {
        throw new Error('Failed to upload detail image')
      }

      updateData.detail_image_url = detailImageUrl
    }
  }

  const { error, data } = await supabaseClient
    .from('creatives')
    .update(updateData)
    .eq('id', id)
    .select()

  if (error) {
    throw error
  }

  return data[0]
}